// Página AdminWarehouses - Gestión de depósitos/sucursales
// Permite crear, editar y gestionar depósitos donde se almacena el stock

import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { getAllWarehousesIncludingInactive, deleteWarehouse } from '../../services/warehouses.service'
import Button from '../../components/Button'
import ConfirmDialog from '../../components/ConfirmDialog'
import Toast from '../../components/Toast'
import './AdminWarehouses.css'

/**
 * Página AdminWarehouses
 * Gestión de depósitos/sucursales para administradores
 */
function AdminWarehouses() {
  const [warehouses, setWarehouses] = useState([])
  const [filteredWarehouses, setFilteredWarehouses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Filtros
  const [activeFilter, setActiveFilter] = useState('all') // 'all', 'active', 'inactive'
  const [searchTerm, setSearchTerm] = useState('')

  // Estados para modales
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [selectedWarehouse, setSelectedWarehouse] = useState(null)
  const [notification, setNotification] = useState(null)

  const fetchWarehouses = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const warehousesData = await getAllWarehousesIncludingInactive()
      setWarehouses(warehousesData)
    } catch (err) {
      console.error('Error cargando depósitos:', err)
      setError('Error al cargar los depósitos')
    } finally {
      setLoading(false)
    }
  }, [])

  const applyFilters = useCallback(() => {
    let filtered = [...warehouses]

    // Filtrar por estado activo/inactivo
    if (activeFilter === 'active') {
      filtered = filtered.filter(w => w.active === true)
    } else if (activeFilter === 'inactive') {
      filtered = filtered.filter(w => w.active === false)
    }

    // Filtrar por búsqueda (nombre, dirección)
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(w => {
        const name = (w.name || '').toLowerCase()
        const address = (w.address || '').toLowerCase()
        return name.includes(search) || address.includes(search)
      })
    }

    setFilteredWarehouses(filtered)
  }, [warehouses, activeFilter, searchTerm])

  // Cargar depósitos
  useEffect(() => {
    fetchWarehouses()
  }, [fetchWarehouses])

  // Aplicar filtros
  useEffect(() => {
    applyFilters()
  }, [applyFilters])


  const handleDelete = async () => {
    if (!selectedWarehouse) return

    try {
      await deleteWarehouse(selectedWarehouse.id)
      
      setNotification({
        type: 'success',
        message: `Depósito "${selectedWarehouse.name}" desactivado correctamente`
      })
      setTimeout(() => setNotification(null), 3000)

      // Recargar depósitos
      await fetchWarehouses()
    } catch (err) {
      console.error('Error eliminando depósito:', err)
      setNotification({
        type: 'error',
        message: 'Error al desactivar el depósito'
      })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setShowDeleteDialog(false)
      setSelectedWarehouse(null)
    }
  }


  if (loading) {
    return (
      <div className="admin-warehouses-page">
        <div className="loading-state">
          <p>Cargando depósitos...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="admin-warehouses-page">
        <div className="error-state">
          <p>{error}</p>
          <Button onClick={fetchWarehouses}>Reintentar</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-warehouses-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      {/* Header */}
      <div className="warehouses-header">
        <div>
          <h1>Gestión de Depósitos</h1>
          <p className="warehouses-subtitle">
            Administra los depósitos y sucursales donde se almacena el stock
          </p>
        </div>
        <Link to="/admin/depositos/nuevo">
          <Button variant="primary">
            ➕ Crear Depósito
          </Button>
        </Link>
      </div>

      {/* Filtros */}
      <div className="warehouses-filters">
        <div className="filter-group">
          <label htmlFor="activeFilter">Estado:</label>
          <select
            id="activeFilter"
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="searchTerm">Buscar:</label>
          <input
            type="text"
            id="searchTerm"
            placeholder="Nombre, dirección..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Lista de depósitos */}
      <div className="warehouses-list">
        {filteredWarehouses.length === 0 ? (
          <div className="empty-state">
            <p>No se encontraron depósitos con los filtros aplicados</p>
          </div>
        ) : (
          <div className="warehouses-grid">
            {filteredWarehouses.map((warehouse) => (
              <div key={warehouse.id} className={`warehouse-card ${!warehouse.active ? 'inactive' : ''}`}>
                <div className="warehouse-card-header">
                  <h3>{warehouse.name}</h3>
                  <span className={`status-badge ${warehouse.active ? 'active' : 'inactive'}`}>
                    {warehouse.active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                
                <div className="warehouse-card-body">
                  {warehouse.address && (
                    <div className="warehouse-info">
                      <span className="info-label">📍</span>
                      <span>{warehouse.address}</span>
                    </div>
                  )}
                  {warehouse.phone && (
                    <div className="warehouse-info">
                      <span className="info-label">📞</span>
                      <span>{warehouse.phone}</span>
                    </div>
                  )}
                  {warehouse.email && (
                    <div className="warehouse-info">
                      <span className="info-label">✉️</span>
                      <span>{warehouse.email}</span>
                    </div>
                  )}
                  {warehouse.notes && (
                    <div className="warehouse-info">
                      <span className="info-label">📝</span>
                      <span className="notes-text">{warehouse.notes}</span>
                    </div>
                  )}
                </div>

                <div className="warehouse-card-footer">
                  <Link to={`/admin/stock?warehouse=${warehouse.id}`}>
                    <Button variant="outline" size="small">
                      Ver Stock
                    </Button>
                  </Link>
                  <div className="warehouse-actions">
                    <Link to={`/admin/depositos/${warehouse.id}`}>
                      <button
                        className="edit-btn"
                        title="Editar depósito"
                      >
                        ✏️
                      </button>
                    </Link>
                    <button
                      className="delete-btn"
                      onClick={() => {
                        setSelectedWarehouse(warehouse)
                        setShowDeleteDialog(true)
                      }}
                      title="Desactivar depósito"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Diálogo de confirmación de eliminación */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Desactivar Depósito"
        message={`¿Estás seguro de que deseas desactivar el depósito "${selectedWarehouse?.name}"? Esta acción no eliminará el depósito, solo lo marcará como inactivo.`}
        confirmText="Desactivar"
        cancelText="Cancelar"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => {
          setShowDeleteDialog(false)
          setSelectedWarehouse(null)
        }}
      />
    </div>
  )
}

export default AdminWarehouses

